import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LogOut, PanelRightOpen, PanelRightClose } from "lucide-react";
import MeetingFlowPanel from "./MeetingFlowPanel";

declare global {
  interface Window {
    JitsiMeetExternalAPI: any;
  }
}

interface JitsiMeetEmbedProps {
  open: boolean;
  onClose: () => void;
  roomName: string;
  displayName: string;
  meetingTitle: string;
  meetingId?: string;
  isChairOrSecretary?: boolean;
  onParticipantJoined?: (participant: { id: string; displayName: string }) => void;
  onParticipantLeft?: (participant: { id: string }) => void;
  onRecordingStarted?: () => void;
  onRecordingStopped?: () => void;
}

export default function JitsiMeetEmbed({
  open,
  onClose,
  roomName,
  displayName,
  meetingTitle,
  meetingId,
  isChairOrSecretary = false,
  onParticipantJoined,
  onParticipantLeft,
}: JitsiMeetEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<any>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [showFlowPanel, setShowFlowPanel] = useState(true);

  // Load Jitsi script
  useEffect(() => {
    if (document.getElementById("jitsi-api-script")) {
      setScriptLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "jitsi-api-script";
    script.src = "https://meet.jit.si/external_api.js";
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize Jitsi when dialog opens
  useEffect(() => {
    if (!open || !scriptLoaded || !containerRef.current || !window.JitsiMeetExternalAPI) return;

    const api = new window.JitsiMeetExternalAPI("meet.jit.si", {
      roomName,
      parentNode: containerRef.current,
      width: "100%",
      height: "100%",
      configOverwrite: {
        subject: meetingTitle,
        startWithAudioMuted: true,
        startWithVideoMuted: false,
        enableClosePage: false,
        disableDeepLinking: true,
        toolbarButtons: [
          "camera", "chat", "closedcaptions", "desktop", "download",
          "filmstrip", "fullscreen", "hangup", "microphone",
          "participants-pane", "raisehand", "recording",
          "select-background", "settings", "shareaudio",
          "sharedvideo", "shortcuts", "tileview", "toggle-camera",
          "videoquality", "whiteboard",
        ],
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        DEFAULT_BACKGROUND: "#1a1a2e",
      },
      userInfo: {
        displayName,
      },
    });

    apiRef.current = api;

    api.addEventListener("readyToClose", () => onClose());

    if (onParticipantJoined) {
      api.addEventListener("participantJoined", onParticipantJoined);
    }
    if (onParticipantLeft) {
      api.addEventListener("participantLeft", onParticipantLeft);
    }

    return () => {
      api.dispose();
      apiRef.current = null;
    };
  }, [open, scriptLoaded, roomName, displayName, meetingTitle]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] max-h-[95vh] p-0 gap-0">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
          <span className="font-semibold text-sm truncate">{meetingTitle}</span>
          <div className="flex items-center gap-2">
            {meetingId && (
              <Button variant="ghost" size="sm" onClick={() => setShowFlowPanel(!showFlowPanel)}>
                {showFlowPanel ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              </Button>
            )}
            <Button variant="destructive" size="sm" onClick={onClose}>
              <LogOut className="h-4 w-4 mr-1" /> Leave
            </Button>
          </div>
        </div>
        <div className="flex flex-1" style={{ height: "calc(95vh - 48px)" }}>
          <div ref={containerRef} className="flex-1 w-full h-full" />
          {meetingId && showFlowPanel && (
            <div className="w-72 border-l overflow-y-auto bg-background">
              <MeetingFlowPanel meetingId={meetingId} isChairOrSecretary={isChairOrSecretary} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
